import { useState } from 'react';
import { Link } from 'react-router';
import { useRailTileClickReport } from '~/ui-rocco/hooks/tile-click-event-for-section';

import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import {
    TileOverlayWithFollowSelector,
    TileMobileActionsSlot,
} from '~/modules/moodboards/components';
import { detailTileActionsFor } from '~/ui-rocco/pages/detail-shared';

/** One project in the staggered run (Figma 149:13332). */
export interface WildPlate {
    id: number;
    title: string;
    link: string;
    src: string;
    architect: string;
    /** The practice that designed it — the enquiry's addressee. */
    architectId?: number;
    architectLink?: string;
    /** Their site, for the panel's Visit site. */
    website?: string;
}

const PLATE_SIZES = getImagesSizes({ desktop: 1080, mobile: 414 });

/**
 * A project, plate and caption: the photograph on the page's 22px corner, and
 * under it the running number, the project and who designed it.
 *
 * The plate's proportions come from the caller — each position in the layout
 * has its own, and that variety is the point of the composition — so this
 * takes the aspect as a class rather than choosing one.
 */
export const InTheWildPlate = ({
    plate,
    aspectClass,
}: {
    plate: WildPlate;
    /** Its place in the run, printed as "01". */
    aspectClass: string;
}) => {
    // The section only checks that a tile has an image URL, so a rendition that
    // has since been deleted or replaced still reaches this plate and 404s.
    // Rocco's own failure state is a red icon meant for authoring surfaces, so
    // drop the image and leave the plate's neutral ground rather than show it.
    const [imageFailed, setImageFailed] = useState(false);
    // The pair the legacy "Projects using this product" rail reported. This
    // plate is neither a Rocco tile nor a DetailTileCard, so it reached neither
    // reporting path and a click on it was recorded nowhere at all.
    const reportClick = useRailTileClickReport(
        'ProductFeaturedProjects',
        'ProjectTile',
    );

    const actions = detailTileActionsFor({
        id: plate.id,
        link: plate.link,
        title: plate.title,
        images: [{ src: plate.src, alt: plate.title }],
        pinType: 'ProjectPin',
        professional: { id: plate.architectId, name: plate.architect },
        professionalLink: plate.architectLink,
        website: plate.website,
    });

    return (
        <div className="relative max-md:w-full">
            <Link
                to={plate.link}
                className="group block no-underline max-md:w-full"
                prefetch="intent"
                onClick={() =>
                    reportClick({
                        id: plate.id,
                        title: plate.title,
                        professionalId: plate.architectId,
                    })
                }
            >
                <div
                    className={cn(
                        'bg-neutral-200 relative w-full overflow-hidden rounded-[1.375rem]',
                        aspectClass,
                    )}
                >
                    {imageFailed ? null : (
                        <Image
                            src={plate.src}
                            alt={plate.title}
                            fill
                            fit="cover"
                            sizes={PLATE_SIZES}
                            fadeIn={false}
                            onError={() => setImageFailed(true)}
                            className="transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                        />
                    )}
                    <TileOverlayWithFollowSelector
                        pinId={plate.id}
                        pinType="ProjectPin"
                        enquiry={actions.enquiry}
                        visitWebsite={actions.visitWebsite}
                        share={actions.share}
                        tileInfo={actions.tileInfo}
                    />
                </div>

                <div className="flex items-start max-md:mt-3 md:mt-[1.25rem]">
                    <span className="flex flex-col max-md:gap-1 md:gap-[0.375rem]">
                        <span className="font-sans font-medium text-[#323232] max-md:text-base md:text-[1.25rem] md:tracking-[-0.006em]">
                            {plate.title}
                        </span>
                        {plate.architect ? (
                            <span className="font-sans text-[#6e6a62] max-md:text-sm md:text-[1rem]">
                                {plate.architect}
                            </span>
                        ) : null}
                    </span>
                </div>
            </Link>
            {/* Mobile actions live outside the plate's link so a tap on the
                `•••` can never navigate to the project. */}
            <TileMobileActionsSlot
                pinId={plate.id}
                pinType="ProjectPin"
                enquiry={actions.enquiry}
                visitWebsite={actions.visitWebsite}
                share={actions.share}
                tileInfo={actions.tileInfo}
            />
        </div>
    );
};
